import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { environment } from '@lohi/environments/environment';
import Fuse from 'fuse.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { fuse } from 'src/app/utilities/fuse';
import { SnackBarService } from '../../shared/snackbar.service';

const searchOptions: Fuse.IFuseOptions<Carrier> = {
  distance: 100,
  keys: ['id', 'name'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

interface Carrier {
  id: string;
  name: string;
}

interface DailyCap {
  id: string;
  carrierId: string;
  date: Date;
  cap: number;
  dayShiftCap: number;
  nightShiftCap: number;
  createdAt: DateTime;
  updatedAt: DateTime;
}

const MAX_COMPANIES_TO_SHOW = 50;

@Component({
  selector: 'lohi-shift-daily-cap',
  templateUrl: './shift-daily-cap.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftDailyCapComponent implements OnInit, OnDestroy {

  public lookupForm: FormGroup;
  public dates: Date[];

  private destroy$$ = new Subject();
  public companySearch = new FormControl();
  public companyPicker = new FormControl();
  public companies$: Observable<Carrier[]>;

  public dailyCaps$$ = new BehaviorSubject<DailyCap[]>([]);

  public start = new FormControl();
  public end = new FormControl();

  constructor(private httpClient: HttpClient, private snackbar: SnackBarService) {
  }

  public ngOnInit() {
    this.capsInit();
    this.getCarriers();

    this.companyPicker.valueChanges
      .pipe(
        takeUntil(this.destroy$$),
        distinctUntilChanged(),
        filter((companyId) => !!companyId),
      )
      .subscribe((companyId) => {
        this.companyPicker.setValue(companyId);
        this.start.reset();
        this.end.reset();
      });

    this.companies$ = combineLatest([
      this.companies$.pipe(map(fuse(searchOptions))),
      this.companySearch.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!fused) {
          return [];
        }
        if (!searchTerm) {
          return fused.data;
        }
        return fused.fuse.search(searchTerm).map((r) => r.item);
      }),
      map((data) => {
        return data.slice(0, Math.min(data.length, MAX_COMPANIES_TO_SHOW));
      }),
    );
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public dateRangeChange(dateRangeStart: HTMLInputElement, dateRangeEnd: HTMLInputElement) {
    this.dates = [];
    const startDate = new Date(dateRangeStart.value);
    const endDate = new Date(dateRangeEnd.value);

    this.getCarrierDailyShiftCaps(this.companyPicker.value, startDate, endDate);

    while (startDate <= endDate) {
      const currentDate = new Date(startDate);
      this.dates.push(currentDate);
      startDate.setDate(startDate.getDate() + 1);
    }
  }

  private capsInit() {
    this.lookupForm = new FormGroup({
      carrierID: new FormControl(),
      range: new FormControl(),
      caps: new FormArray([]),
    });
  }

  public submit() {
    const capInputs = document.querySelectorAll('input[name="valCap[]"]') as NodeListOf<HTMLInputElement>;
    const dateInputs = document.querySelectorAll('input[name="date[]"]') as NodeListOf<HTMLInputElement>;
    const capsData = [];

    for (let i = 0; i < capInputs.length; i++) {
      const capValue = capInputs[i].value ? parseInt(capInputs[i].value, 10) : null;
      const dateValue = dateInputs[i].value;

      if (capValue !== null) {
        capsData.push({
          date: dateValue,
          cap: capValue,
          dayShiftCap: Math.floor(capValue / 2),
          nightShiftCap: Math.ceil(capValue / 2),
        });
      }
    }

    const submitData = {
      carrierId: this.companyPicker.value,
      caps: capsData,
    };

    if (submitData.carrierId === null || submitData.caps.length === 0) {
      return;
    }

    this.httpClient
      .post<boolean>(`${environment.api}/v2/internal/daily_shift_cap`, submitData)
      .subscribe({
        error: (err) => {
          this.snackbar.showError(err);
        },
        next: (resp) => {
          this.snackbar.showMessage('Daily cap saved');
          return resp;
        },
      });
  }

  private getCarriers(): void {
    this.companies$ = this.httpClient
      .get<Carrier[]>(`${environment.api}/v2/internal/daily_shift_cap/carrier_list`);
  }

  private getCarrierDailyShiftCaps(carrierId: string, startDate: Date, endDate: Date): void {
    if (!startDate || !endDate || isNaN(startDate.getDate()) || isNaN(endDate.getDate())) {
      return;
    }

    let params: HttpParams;
    const startDateString = this.formatDate(startDate);
    const endDateString = this.formatDate(endDate);
    if (this.companyPicker.value) {
      params = new HttpParams()
        .set('carrierId', carrierId)
        .set('dateRangeStart', startDateString)
        .set('dateRangeEnd', endDateString);
    }

    this.httpClient
      .get<DailyCap[]>(`${environment.api}/v2/internal/daily_shift_cap/daily_caps`, { params })
      .subscribe({
        next: (data) => {
          this.dailyCaps$$.next(data);
        },
        error: (err) => {
          this.snackbar.showError(err);
        },
      });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // returns "YYYY-MM-DD" format
  }

}
